import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { CircularProgress, FormHelperText } from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import Resizer from 'react-image-file-resizer';
import { useTypedTranslation } from '@app/hooks';
import { AxiosPromise } from 'axios';
import { TranslationsTypes } from '@app/models';
import { PasslyButton } from '@app/components';

interface IProps {
    currentImageUrl?: string;
    getCatalogImages?: (handleError: Function, handleSuccess: Function) => AxiosPromise;
    onImageChange: (data: string) => void;
    width?: number;
    height?: number;
    onFileUpload: (data: any, handleError: Function, handleSuccess: Function) => AxiosPromise;
    placeholder?: string;
    imageRequirements?: TranslationsTypes;
    dimensionsInfo?: TranslationsTypes;
    label?: string;
}

interface IBlobFileObject extends Blob {
    lastModified?: Date;
    name?: string;
}

interface ImageUploadState {
    image?: Blob & IBlobFileObject | null;
    imageUploadUrl?: string;
    error?: boolean;
    loading?: boolean;
    catalogImages?: string[];
}

interface ImageUploadSettings {
    width: number;
    height: number;
    allowedType: AllowedImageTypes[];
    objectType: 'blob';
    requirements: TranslationsTypes;
    dimensionsInfo: TranslationsTypes;
}

interface IBranding {
    imageUrl?: string;
}

enum AllowedImageTypes {
    png = 'image/png',
    jpeg = 'image/jpeg',
    gif = 'image/gif'
}

export const ImageUpload: React.SFC<IProps> = ({ width, height, getCatalogImages, currentImageUrl, onImageChange, onFileUpload, placeholder, imageRequirements, dimensionsInfo, label }: IProps) => {

    const UPLOAD_IMAGE_SETTINGS: ImageUploadSettings = {
        width: width ? width : 150,
        height: height ? height : 50,
        allowedType: [AllowedImageTypes.png, AllowedImageTypes.jpeg, AllowedImageTypes.gif],
        objectType: 'blob',
        requirements: imageRequirements ? imageRequirements : 'APP_ICON_UPLOAD_ERROR',
        dimensionsInfo: dimensionsInfo ? dimensionsInfo : '' // TODO: need to check all image uploader occurences for this to resolve
    };

    const { translation } = useTypedTranslation();

    // TODO: better use svg, smaller size
    const placeholderImage: string = placeholder ?
        placeholder :
        `data:image/svg+xml,%3Csvg width='150' height='50' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M150 0H0v50h150V0zm-5 5H5v40h140V5z' fill='%23878787'/%3E%3Ccircle cx='87.854' cy='17.919' r='3.866' fill='%23878787'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M54.313 36.946v-7.041l10.56-10.56L76.22 30.69l6.285-6.285L95 36.901l-40.687.045z' fill='%23878787'/%3E%3C/svg%3E`;
    const ImageUploadInitialState: ImageUploadState = {
        image: null,
        imageUploadUrl: currentImageUrl ? currentImageUrl : '',
        error: false,
        loading: false,
        catalogImages: []
    };

    const [{ image, imageUploadUrl, error, loading, catalogImages }, setState] = useState(ImageUploadInitialState);
    const unmounted = useRef(false);

    useEffect(() => {
        if (getCatalogImages)
            getCatalogImages(handleCatalogLoadError, handleCatalogLoadSuccess);

        return () => { unmounted.current = true; };
    }, []);

    useEffect(() => {
        uploadFile();
    }, [image]);

    // NEW IMAGE
    // UPLOADED IMAGE RESIZING
    const isOfValidType = ((imageFileType: string): boolean => UPLOAD_IMAGE_SETTINGS.allowedType.includes(imageFileType as AllowedImageTypes));

    const resizeImage = (imageFile: File): void => {

        if (!isOfValidType(imageFile.type)) {
            setState(prevState => ({ ...prevState, error: true }));
            return;
        } else {
            setState(prevState => ({ ...prevState, error: false }));
        }

        const payload = (payload: IBlobFileObject) => {
            payload.lastModified = new Date();
            payload.name = imageFile.name;
            setState(prevState => ({ ...prevState, image: payload }));
        };

        const { width, height, objectType } = UPLOAD_IMAGE_SETTINGS;
        Resizer.imageFileResizer(
            imageFile,
            width,
            height,
            'png',
            100,
            0,
            payload,
            objectType
        );
    };

    const handleUploadSuccess = ({ data }: any) => {
        setState(prevState => ({ ...prevState, imageUploadUrl: data.Url, loading: false }));
        onImageChange(data.Url);
    };

    const handleUploadError = () => {
        setState(prevState => ({ ...prevState, error: true, loading: false }));
    };

    const uploadFile = () => {
        if (image) {
            let formData = new FormData();
            formData.append('file', image, image.name);
            setState(prevState => ({ ...prevState, loading: true }));
            onFileUpload(formData, handleUploadError, handleUploadSuccess); // post handled by external function
        }
    };

    // EXISTING CATALOG IMAGES
    const handleCatalogLoadSuccess = ({ data }: any) => {
        if (!unmounted.current)
            setState(prevState => ({ ...prevState, catalogImages: data.value }));
    };

    const handleCatalogLoadError = () => {
        if (!unmounted.current)
            setState(prevState => ({ ...prevState, error: true }));
    };

    // INTERFACE ACTIONS
    const handleOnInputChange = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
        if (files && files.length) {
            Array.from(files)
                .forEach((file: File) => {
                    resizeImage(file);
                });
        }
    };

    const handleCatalogItemSelected = (catalogImageUrl: string): void => {
        setState(prevState => ({ ...prevState, imageUploadUrl: catalogImageUrl }));
        onImageChange(catalogImageUrl);
    };

    return (
        <div className='image-upload'>
            <div className='image-uploader'>
                <span className={'text-field__label'}>{translation((label ?? '') as TranslationsTypes)}</span>
                <div className='image-uploader__image-wrapper'
                    style={{
                        height: UPLOAD_IMAGE_SETTINGS.height
                    }}>
                    <img className='image-uploader__image'
                        src={
                            imageUploadUrl ?
                                imageUploadUrl :
                                placeholderImage
                        }
                        alt={translation('CUSTOM_IMAGE')}
                    />
                </div>
                <div className='image-uploader__dimension-info'>{translation(UPLOAD_IMAGE_SETTINGS.dimensionsInfo)}</div>
                <PasslyButton variant='contained'
                    color='primary'
                    component='label'
                    disabled={loading}
                    startIcon={<PublishIcon />}
                >
                    <input
                        className='image-uploader__upload-input'
                        type='file'
                        name='fileInput'
                        id='file'
                        accept={UPLOAD_IMAGE_SETTINGS.allowedType.join(',')}
                        onChange={handleOnInputChange}
                    />{translation('UPLOAD')}
                    {loading && <CircularProgress size={24} className='button--loading__bar' />}
                    {error && <FormHelperText className='image-uploader__error'>{translation(UPLOAD_IMAGE_SETTINGS.requirements)}</FormHelperText>}
                </PasslyButton>
            </div>
            {
                catalogImages && catalogImages.length ?
                    <div className='image-catalog'>
                        <h3>{translation('SELECT_FROM_IMAGE_CATALOG')}</h3>
                        {catalogImages.map(({ Url }: any, index: number) => (
                            <img className='image-catalog__image' src={Url} key={`${index}`} onClick={() => handleCatalogItemSelected(Url)} />
                        ))}
                    </div> : ''
            }
        </div>
    );
};
